// src/pages/Login/LoginPage.js
import React, { useState } from "react";
import { Button, Input, Form, message } from "antd";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import { Sparkles } from "lucide-react";
import authApi from "../../api/authApi";
import logo from "../../assets/img/logo.png";

const inputClass =
    "!rounded-xl !border-white/10 !bg-white/[0.06] !py-2.5 !text-white/90 !shadow-none hover:!border-white/[0.18] focus:!border-emerald-400/50 [&_.ant-input]:!bg-transparent [&_.ant-input]:!text-white/90 [&_.ant-input::placeholder]:!text-white/35";

const LoginPage = ({ onLogin }) => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (values) => {
        setLoading(true);
        try {
            const res = await authApi.login({
                email: values.email,
                password: values.password,
            });
            if (res?.user?.role) localStorage.setItem("role", res.user.role);
            message.success("Вход выполнен");
            onLogin?.();
            navigate("/dashboard", { replace: true });
        } catch (e) {
            console.error("Login failed:", e);
            message.error("Неверный email или пароль");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-[100dvh] overflow-hidden bg-[#070A12] text-white antialiased">
            <div className="pointer-events-none fixed inset-0 z-0" aria-hidden>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(52,211,153,0.12),transparent_55%)]" />
                <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-emerald-500/15 blur-[100px]" />
                <div className="absolute -right-24 top-1/4 h-[380px] w-[380px] rounded-full bg-indigo-500/20 blur-[90px]" />
                <div className="absolute bottom-0 left-1/3 h-[320px] w-[320px] rounded-full bg-fuchsia-500/10 blur-[80px]" />
                <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage: `linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)`,
                        backgroundSize: "48px 48px",
                    }}
                />
            </div>

            <div className="relative z-10 flex min-h-[100dvh] items-center justify-center px-4 py-10 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 24, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full max-w-[440px]"
                >
                    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.05] p-8 shadow-[0_24px_80px_-32px_rgba(0,0,0,0.95)] backdrop-blur-2xl sm:p-10">
                        <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-emerald-400/10 blur-3xl" />
                        <div className="pointer-events-none absolute -bottom-20 -left-12 h-36 w-36 rounded-full bg-indigo-500/10 blur-3xl" />

                        <div className="relative flex flex-col items-center text-center">
                            <motion.div
                                initial={{ scale: 0.85, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.08, duration: 0.45, ease: "easeOut" }}
                                className="relative mb-5"
                            >
                                <div className="absolute inset-0 rounded-full bg-emerald-400/20 blur-xl" />
                                <div className="relative flex h-[88px] w-[88px] items-center justify-center rounded-full border border-white/10 bg-white/[0.06] p-2 shadow-inner">
                                    <img src={logo} alt="OctoAdmin" className="h-16 w-16 rounded-full object-cover" />
                                </div>
                            </motion.div>

                            <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-emerald-200/90">
                                <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                                Панель управления
                            </div>
                        </div>

                        <Form
                            layout="vertical"
                            requiredMark={false}
                            onFinish={handleLogin}
                            className="login-page-form relative mt-7"
                            size="large"
                        >
                            <Form.Item
                                label={<span className="text-[13px] font-medium text-white/55">Email</span>}
                                name="email"
                                rules={[
                                    { required: true, message: "Введите email" },
                                    { type: "email", message: "Некорректный формат email" },
                                ]}
                                className="!mb-5"
                            >
                                <Input
                                    autoComplete="email"
                                    placeholder="введите логин"
                                    prefix={<MailOutlined className="text-emerald-400/75" />}
                                    className={inputClass}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="text-[13px] font-medium text-white/55">Пароль</span>}
                                name="password"
                                rules={[{ required: true, message: "Введите пароль" }]}
                                className="!mb-6"
                            >
                                <Input.Password
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    prefix={<LockOutlined className="text-emerald-400/75" />}
                                    className={inputClass}
                                />
                            </Form.Item>

                            <Form.Item className="!mb-0">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    className="!h-12 !w-full !rounded-xl !border-0 !bg-emerald-500 !text-[15px] !font-semibold !text-[#070A12] !shadow-[0_12px_40px_-12px_rgba(52,211,153,0.65)] transition hover:!bg-emerald-400 hover:!text-[#070A12]"
                                >
                                    Войти
                                </Button>
                            </Form.Item>
                        </Form>

                        <p className="relative mt-8 text-center text-[12px] text-white/30">
                            Защищённое соединение · только для администраторов
                        </p>
                    </div>

                    <p className="mt-6 text-center text-[12px] text-white/25">OctoAdmin</p>
                </motion.div>
            </div>
        </div>
    );
};

export default LoginPage;
